import React, { useRef, useState, useEffect } from "react";
import "../styles/SetSection.css";
import { useDispatch } from "react-redux";
import { SetPopupReducerData } from "../store/reducer/PopupReducer";
import { io } from "socket.io-client";
import { useVisionContext } from "../Components/ContextAPI/VisionContext";
import { toast } from "react-toastify";
import { setEventRecord } from "../api/api";
import CustomButton from "../ui/custom-button";
import { PulseLoader } from "react-spinners";

// const socket = io("http://192.168.20.24:8000", {
//   reconnectionAttempts: 1,
//   reconnectionDelay: 500,
// });

const SetDemo = () => {
  const dispatch = useDispatch();

  const [drawingEnabled, setDrawingEnabled] = useState(false);
  const [drawMode, setDrawMode] = useState("line");
  const [isDrawing, setIsDrawing] = useState(false);
  const [lines, setLines] = useState([]);
  const [currentLine, setCurrentLine] = useState([]);
  const [lastLine, setLastLine] = useState(null);
  const [lineOrientation, setLineOrientation] = useState(null);
  const [showDirectionSelect, setShowDirectionSelect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [restrictedType, setRestrictedType] = useState(null);

  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const audioRef = useRef(null);

  const cameraId = "685c00a2a178e25cf8560983";

  const { visionData, updateState } = useVisionContext();

  const getCanvasPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleDrawClick = () => {
    setDrawingEnabled(true);
    setLines([]);
    setCurrentLine([]);
  };

  const handleMouseDown = (e) => {
    if (!drawingEnabled) return;
    const pos = getCanvasPos(e);
    if (drawMode === "line") {
      setIsDrawing(true);
      setCurrentLine([pos]);
    } else {
      setCurrentLine((prev) => [...prev, pos]);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || drawMode !== "line" || !drawingEnabled) return;
    setCurrentLine((prev) => [...prev, getCanvasPos(e)]);
  };

  const handleMouseUp = () => {
    if (!drawingEnabled) return;

    if (drawMode === "line" && currentLine.length > 1) {
      const start = currentLine[0];
      const end = currentLine[currentLine.length - 1];
      const dx = Math.abs(end.x - start.x);
      const dy = Math.abs(end.y - start.y);
      const THRESHOLD = 10;

      let orientation = dx > dy ? "horizontal" : "vertical";
      if (dx < THRESHOLD && dy >= THRESHOLD) orientation = "vertical";
      if (dy < THRESHOLD && dx >= THRESHOLD) orientation = "horizontal";

      setLines((prev) => [...prev, currentLine]);
      setLastLine({ x1: start.x, y1: start.y, x2: end.x, y2: end.y });
      setLineOrientation(orientation);
      setShowDirectionSelect(true);
      setCurrentLine([]);
    }
    setIsDrawing(false);
  };

  const handleCompletePolygon = () => {
    if (drawMode === "polygon" && currentLine.length >= 3) {
      const polygonPoints = currentLine.map((p) => [
        Math.round(p.x),
        Math.round(p.y),
      ]);
      //console.log("polygonPoints", polygonPoints);

      setLines((prev) => [...prev, currentLine]);
      setCurrentLine([]);
      setDrawingEnabled(false);
    }
  };

  const handleDirectionSelect = (e) => {
    const direction = e.target.value;
    setRestrictedType(direction);
    setShowDirectionSelect(false);
    setLastLine(null);
  };

  const handleClickSave = async () => {
    // if (lines.length === 0) {
    //   alert("No area drawn.");
    //   return;
    // }
    setLoading(true);

    const latestLine = lines[lines.length - 1];
    const coordinates = latestLine.map((point) => [
      Math.round(point.x),
      Math.round(point.y),
    ]);

    const payload = {
      camera_id: cameraId,
      coordinates,
      shape_type: drawMode,
      action_type_ids: visionData?.actionTypeIds || [],
      alert_type_ids: visionData?.alertTypeIds || [],
    };

    try {
      const events = await setEventRecord(payload);

      if (events) {
        toast("Saved successfully!");

        setDrawingEnabled(false);
        setLines([]);
        setCurrentLine([]);

        setLoading(false);
      } else {
      }
    } catch (error) {
      //console.log("error>catch", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const drawLines = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "red";

      lines.forEach((line) => {
        ctx.beginPath();
        line.forEach((point, index) => {
          index === 0
            ? ctx.moveTo(point.x, point.y)
            : ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      });

      if (currentLine.length > 0) {
        ctx.beginPath();
        currentLine.forEach((point, index) => {
          index === 0
            ? ctx.moveTo(point.x, point.y)
            : ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      }
    };

    drawLines();
  }, [lines, currentLine]);

  return (
    <div className="set-section-container">
      <h3 className="section-title">Set Section (Camera01)</h3>

      <div className="image-area-wrapper">
        <div
          className="image-container"
          ref={imageRef}
          style={{ position: "relative", width: "960px", height: "540px" }}
        >
          <img
            src="https://visionemptrack.greymatterz.com/stream2/stream2/685c00a2a178e25cf8560983"
            alt="Camera Feed"
            className="camera-image"
          />

          {drawingEnabled && (
            <canvas
              ref={canvasRef}
              width={960}
              height={540}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                cursor: drawMode === "polygon" ? "crosshair" : "default",
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            />
          )}
        </div>

        <audio ref={audioRef} src="/alert.mp3" preload="auto" />

        <div className="button-group">
          {drawingEnabled && (
            <select
              value={drawMode}
              onChange={(e) => setDrawMode(e.target.value)}
              style={{ padding: "4px", marginRight: "8px" }}
            >
              <option value="line">Line</option>
              <option value="polygon">Polygon</option>
            </select>
          )}

          <button className="action-btn" onClick={handleDrawClick}>
            Draw Area
          </button>

          {drawMode === "polygon" && currentLine.length >= 3 && (
            <button className="action-btn" onClick={handleCompletePolygon}>
              Complete Polygon
            </button>
          )}

          <button
            className="action-btn"
            onClick={() => {
              setDrawingEnabled(false);
              dispatch(
                SetPopupReducerData({ modalType: "setalert", showModal: true })
              );
            }}
          >
            Set Alert
          </button>

          <button
            className="action-btn"
            onClick={() => {
              setDrawingEnabled(false);
              dispatch(
                SetPopupReducerData({ modalType: "setaction", showModal: true })
              );
            }}
          >
            Set Action
          </button>
        </div>
      </div>

      {showDirectionSelect && (
        <div className="direction-popup">
          <label htmlFor="direction">Select Restricted Side</label>
          <select
            id="direction"
            onChange={handleDirectionSelect}
            defaultValue=""
          >
            <option value="" disabled>
              Choose direction
            </option>
            {lineOrientation === "horizontal" && (
              <>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
                <option value="both">Both Sides</option>
              </>
            )}
            {lineOrientation === "vertical" && (
              <>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </>
            )}
          </select>
        </div>
      )}

      <div className="footer-buttons">
        <button className="cancel-btn">Cancel</button>
        {loading ? (
          <CustomButton
            type="submit"
            className="my-2"
            width={"100%"}
            size="large"
            //   loading={loading}
          >
            <PulseLoader color="#fff" />
          </CustomButton>
        ) : (
          <button className="save-btn" onClick={handleClickSave}>
            Save
          </button>
        )}
      </div>
    </div>
  );
};

export default SetDemo;
